import {
  AppSessionCellId,
  AppSessionEventState,
  AppSessionEventStateIndices,
  AppSessionId,
  CellId,
  ExhaustiveAppSessionEvent,
  ExportTypeAndStatus,
  Output,
  OutputContent,
  OutputContentId,
  OutputId,
  asciiCompare,
  createAppSessionEventStateIndices,
  getExportTypesAndStatus,
  notEmpty,
  stableEmptyArray,
  stableEmptyObject,
  typedObjectEntries,
  typedObjectFromEntries,
  typedObjectValues,
  updateDraftAppSessionEventState,
} from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { omitBy } from "lodash";
import { shallowEqual } from "../../util/index.js";

import { RootState } from "../store";
import {
  createSelectorV2,
  createShallowResultSelectorV2,
} from "../utils/index.js";

import {
  OutputContentLoadStatus,
  OutputContentValue,
  outputContentSelectors,
} from "./outputContentSlice";

export type OutputWithContent = Output & {
  outputContents: readonly OutputContent[];
};

export type ResolvedOutputContent = OutputContent & OutputContentValue;

export type OutputWithResolvedContent = Output & {
  contents: ResolvedOutputContent[];
  exportTypesAndStatus: ExportTypeAndStatus[];
};

/**
 * Encapsulates the loading state and metadata for app session even state
 */
export type AppSessionEventSliceValue = {
  numberOfSubscribers: number;
} & (
  | {
      type: "initial";
      initialRevision: undefined;
      state: undefined;
      indices: undefined;
    }
  | {
      type: "loading";
      initialRevision: undefined;
      state: undefined;
      indices: undefined;
    }
  | {
      type: "error";
      initialRevision: undefined;
      state: undefined;
      indices: undefined;
    }
  | {
      type: "success";
      initialRevision: number;
      state: AppSessionEventState;
      indices: AppSessionEventStateIndices;
    }
);

const makeInitialValue = (): AppSessionEventSliceValue => ({
  type: "initial",
  initialRevision: undefined,
  state: undefined,
  indices: undefined,
  numberOfSubscribers: 0,
});

/**
 * A mapping of app session id to app session event state, its loading state, and useful metadata
 */
export interface AppSessionEventSliceState {
  [appSessionId: AppSessionId]: AppSessionEventSliceValue | undefined;
}

const initialAppSessionEventSliceState: AppSessionEventSliceState = {};

const appSessionEventSlice = createSlice({
  name: "appSessionEvent",
  initialState: initialAppSessionEventSliceState,
  reducers: {
    subscribe: (
      state: AppSessionEventSliceState,
      {
        payload: { appSessionId },
      }: PayloadAction<{ appSessionId: AppSessionId }>,
    ) => {
      const prev = (state[appSessionId] ??= makeInitialValue());
      prev.numberOfSubscribers++;
    },
    unsubscribe: (
      state: AppSessionEventSliceState,
      {
        payload: { appSessionId },
      }: PayloadAction<{ appSessionId: AppSessionId }>,
    ) => {
      const prev = state[appSessionId];
      if (prev != null) {
        prev.numberOfSubscribers--;
        // evict if there are no more subscribers
        if (prev.numberOfSubscribers === 0) {
          delete state[appSessionId];
        }
      }
    },
    setLoading: (
      state: AppSessionEventSliceState,
      {
        payload: { appSessionId },
      }: PayloadAction<{
        appSessionId: AppSessionId;
      }>,
    ) => {
      const prev = (state[appSessionId] ??= makeInitialValue());
      prev.type = "loading";
    },
    setError: (
      state: AppSessionEventSliceState,
      {
        payload: { appSessionId },
      }: PayloadAction<{
        appSessionId: AppSessionId;
      }>,
    ) => {
      const prev = (state[appSessionId] ??= makeInitialValue());
      prev.type = "error";
    },
    setSuccess: (
      state: AppSessionEventSliceState,
      {
        payload: { appSessionId, initialRevision, initialState },
      }: PayloadAction<{
        appSessionId: AppSessionId;
        initialRevision: number;
        initialState: AppSessionEventState;
      }>,
    ) => {
      const prev = (state[appSessionId] ??= makeInitialValue());
      prev.type = "success";
      prev.initialRevision = initialRevision;
      prev.state = initialState;
      prev.indices = createAppSessionEventStateIndices(initialState);
    },
    handleEvents: (
      state: AppSessionEventSliceState,
      {
        payload: { appSessionId, events },
      }: PayloadAction<{
        appSessionId: AppSessionId;
        events: readonly ExhaustiveAppSessionEvent[];
      }>,
    ) => {
      const prev = state[appSessionId];
      if (prev == null || prev.type !== "success") {
        throw new Error(
          `Event state not found for app session ${appSessionId}`,
        );
      } else {
        updateDraftAppSessionEventState({
          state: prev.state,
          indices: prev.indices,
          events,
        });
      }
    },
  },
});

export const appSessionEventSliceReducer = appSessionEventSlice.reducer;

export const appSessionEventSliceActions = appSessionEventSlice.actions;

export const appSessionEventSliceSelectors = {
  select: (state: RootState) => state.appSessionEvent,
  selectValue: (state: RootState, appSessionId: AppSessionId) =>
    appSessionEventSliceSelectors.select(state)[appSessionId],
  selectValueOrThrow: (state: RootState, appSessionId: AppSessionId) => {
    const value = appSessionEventSliceSelectors.selectValue(
      state,
      appSessionId,
    );

    if (value == null) {
      throw new Error(
        `Event slice value not found for app session ${appSessionId}`,
      );
    } else {
      return value;
    }
  },
  selectLoadedValueOrThrow: (state: RootState, appSessionId: AppSessionId) => {
    const value = appSessionEventSliceSelectors.selectValueOrThrow(
      state,
      appSessionId,
    );

    if (value.type !== "success") {
      throw new Error(
        `Event slice value state is '${value.type}' for app session ${appSessionId}`,
      );
    } else {
      return value;
    }
  },
  selectEventState: (state: RootState, appSessionId: AppSessionId) =>
    appSessionEventSliceSelectors.selectValue(state, appSessionId)?.state,
  selectEventStateOrThrow: (state: RootState, appSessionId: AppSessionId) =>
    appSessionEventSliceSelectors.selectLoadedValueOrThrow(state, appSessionId)
      .state,
  selectIndices: (state: RootState, appSessionId: AppSessionId) =>
    appSessionEventSliceSelectors.selectValue(state, appSessionId)?.indices,
  selectIndicesOrThrow: (state: RootState, appSessionId: AppSessionId) =>
    appSessionEventSliceSelectors.selectLoadedValueOrThrow(state, appSessionId)
      .indices,
};

const root = {
  safeSelect: (state: RootState, appSessionId: AppSessionId) =>
    appSessionEventSliceSelectors.selectValue(state, appSessionId)?.state,
  select: (state: RootState, appSessionId: AppSessionId) =>
    appSessionEventSliceSelectors.selectEventStateOrThrow(state, appSessionId),
  selectCellIdToAppSessionCellId: (
    state: RootState,
    appSessionId: AppSessionId,
  ) =>
    appSessionEventSliceSelectors.selectIndices(state, appSessionId)
      ?.cellIdToAppSessionCellId ?? stableEmptyObject(),
  selectAppSessionCellIdToOutputIds: (
    state: RootState,
    appSessionId: AppSessionId,
  ) =>
    appSessionEventSliceSelectors.selectIndices(state, appSessionId)
      ?.appSessionCellIdToOutputIds ?? stableEmptyObject(),
  selectOutputIdToOutputContentIds: (
    state: RootState,
    appSessionId: AppSessionId,
  ) =>
    appSessionEventSliceSelectors.selectIndices(state, appSessionId)
      ?.outputIdToOutputContentIds ?? stableEmptyObject(),
};

const appSession = {
  safeSelect: (state: RootState, appSessionId: AppSessionId) =>
    root.safeSelect(state, appSessionId)?.appSession,
  select: (state: RootState, appSessionId: AppSessionId) =>
    root.select(state, appSessionId).appSession,
};

const selectAppSessionCells = (state: RootState, appSessionId: AppSessionId) =>
  root.safeSelect(state, appSessionId)?.appSessionCells;

const appSessionCells = {
  select: selectAppSessionCells,
  selectArray: createSelectorV2(selectAppSessionCells, (entities) =>
    entities != null
      ? typedObjectValues(entities).sort((a, b) => asciiCompare(a.id, b.id))
      : null,
  ),
  selectById: (
    state: RootState,
    appSessionId: AppSessionId,
    appSessionCellId: AppSessionCellId,
  ) => appSessionCells.select(state, appSessionId)?.[appSessionCellId],
  selectByCellId: (
    state: RootState,
    appSessionId: AppSessionId,
    cellId: CellId,
  ) => {
    const appSessionCellId = root.selectCellIdToAppSessionCellId(
      state,
      appSessionId,
    )[cellId];
    if (appSessionCellId == null) {
      return undefined;
    } else {
      return appSessionCells.selectById(state, appSessionId, appSessionCellId);
    }
  },
};

const stableOutputContentsCache = new WeakMap<
  Output,
  readonly OutputContent[]
>();
const getStableOutputContents = (
  output: Output,
  contents: readonly OutputContent[],
): readonly OutputContent[] => {
  const prevVal = stableOutputContentsCache.get(output);

  if (prevVal != null && shallowEqual(prevVal, contents)) {
    return prevVal;
  } else {
    stableOutputContentsCache.set(output, contents);
    return contents;
  }
};

const stableOutputWithContentCache = new WeakMap<
  Output,
  WeakMap<readonly OutputContent[], OutputWithContent>
>();
const getStableOutputWithContent = (
  output: Output,
  outputContents: readonly OutputContent[],
): OutputWithContent => {
  const stableOutputContents = getStableOutputContents(output, outputContents);

  let innerCache = stableOutputWithContentCache.get(output);
  if (innerCache == null) {
    innerCache = new WeakMap();
    stableOutputWithContentCache.set(output, innerCache);
  }

  const prevVal = innerCache.get(stableOutputContents);

  if (prevVal != null) {
    return prevVal;
  } else {
    const newVal = {
      ...output,
      outputContents: stableOutputContents,
    };
    innerCache.set(stableOutputContents, newVal);
    return newVal;
  }
};

const selectOutputs = (state: RootState, appSessionId: AppSessionId) =>
  root.safeSelect(state, appSessionId)?.outputs;

const selectOutputContents = (state: RootState, appSessionId: AppSessionId) =>
  root.safeSelect(state, appSessionId)?.outputContents;

const selectOutputsWithContent = createSelectorV2(
  selectOutputs,
  selectOutputContents,
  root.selectOutputIdToOutputContentIds,
  (outputs, outputContents, index) =>
    typedObjectFromEntries(
      typedObjectEntries(outputs ?? {}).map(([outputId, output]) => [
        outputId,
        output != null
          ? getStableOutputWithContent(
              output,
              index[outputId]
                ?.map((cId) => outputContents?.[cId])
                .filter(notEmpty) ?? stableEmptyArray<OutputContent>(),
            )
          : undefined,
      ]),
    ),
);

const outputsWithContent = {
  select: selectOutputsWithContent,
  selectArray: createSelectorV2(selectOutputsWithContent, (entities) =>
    entities != null
      ? typedObjectValues(entities)
          .filter(notEmpty)
          .sort((a, b) => asciiCompare(a.id, b.id))
      : null,
  ),
  selectById: (
    state: RootState,
    appSessionId: AppSessionId,
    outputId: OutputId,
  ) => outputsWithContent.select(state, appSessionId)?.[outputId],
  createSelectByAppSessionCellId: (appSessionCellId: AppSessionCellId) =>
    createShallowResultSelectorV2(
      selectOutputsWithContent,
      (state: RootState, appSessionId: AppSessionId) =>
        root.selectAppSessionCellIdToOutputIds(state, appSessionId)[
          appSessionCellId
        ],
      (entities, outputIds) =>
        outputIds
          ?.map((id) => entities?.[id])
          .filter(notEmpty)
          .sort((a, b) => a.order - b.order) ??
        stableEmptyArray<OutputWithContent>(),
    ),
};

const stableResolvedOutputContentCache = new WeakMap<
  OutputContent,
  WeakMap<OutputContentValue, ResolvedOutputContent>
>();
const getStableResolvedOutputContent = (
  outputContent: OutputContent,
  contentValue: OutputContentValue,
): ResolvedOutputContent => {
  let innerCache = stableResolvedOutputContentCache.get(outputContent);
  if (innerCache == null) {
    innerCache = new WeakMap();
    stableResolvedOutputContentCache.set(outputContent, innerCache);
  }

  const prevVal = innerCache.get(contentValue);

  if (prevVal != null) {
    return prevVal;
  } else {
    const newVal = {
      ...outputContent,
      ...contentValue,
    };
    innerCache.set(contentValue, newVal);
    return newVal;
  }
};

const stableResolvedOutputContentsCache = new WeakMap<
  OutputWithContent,
  ResolvedOutputContent[]
>();
const getStableResolvedOutputContents = (
  output: OutputWithContent,
  contents: ResolvedOutputContent[],
): ResolvedOutputContent[] => {
  const prevVal = stableResolvedOutputContentsCache.get(output);

  if (prevVal != null && shallowEqual(prevVal, contents)) {
    return prevVal;
  } else {
    stableResolvedOutputContentsCache.set(output, contents);
    return contents;
  }
};

const stableOutputWithResolvedContentCache = new WeakMap<
  OutputWithContent,
  WeakMap<ResolvedOutputContent[], OutputWithResolvedContent>
>();
const getStableOutputWithResolvedContent = (
  output: OutputWithContent,
  outputContentValues: Record<OutputContentId, OutputContentValue | undefined>,
): OutputWithResolvedContent => {
  const resolvedOutputContents = output.outputContents.map((c) =>
    getStableResolvedOutputContent(
      c,
      outputContentValues[c.id] ?? {
        id: c.id,
        status: OutputContentLoadStatus.NO_CONTENT,
        contents: undefined,
      },
    ),
  );
  const stableOutputContents = getStableResolvedOutputContents(
    output,
    resolvedOutputContents,
  );

  let innerCache = stableOutputWithResolvedContentCache.get(output);
  if (innerCache == null) {
    innerCache = new WeakMap();
    stableOutputWithResolvedContentCache.set(output, innerCache);
  }

  const prevVal = innerCache.get(stableOutputContents);

  if (prevVal != null) {
    return prevVal;
  } else {
    const newVal = {
      ...output,
      contents: stableOutputContents,
      exportTypesAndStatus: stableOutputContents.every(
        (c) => c.status === "READY",
      )
        ? getExportTypesAndStatus(stableOutputContents)
        : [],
    };
    innerCache.set(stableOutputContents, newVal);
    return newVal;
  }
};

const selectOutputsWithResolvedContent = createSelectorV2(
  selectOutputsWithContent,
  (state: RootState, appSessionId: AppSessionId) =>
    outputContentSelectors.selectAppSessionValueOrThrow(state, appSessionId)
      .outputContents,
  (
    outputs,
    outputContents,
  ): Record<OutputId, OutputWithResolvedContent | undefined> =>
    typedObjectFromEntries(
      typedObjectEntries(outputs ?? {}).map(([outputId, output]) => [
        outputId,
        output != null
          ? getStableOutputWithResolvedContent(output, outputContents)
          : undefined,
      ]),
    ),
);

const outputsWithResolvedContent = {
  select: selectOutputsWithResolvedContent,
  selectArray: createSelectorV2(selectOutputsWithResolvedContent, (entities) =>
    entities != null
      ? typedObjectValues(entities)
          .filter(notEmpty)
          .sort((a, b) => asciiCompare(a.id, b.id))
      : null,
  ),
  selectById: (
    state: RootState,
    appSessionId: AppSessionId,
    outputId: OutputId,
  ) => outputsWithResolvedContent.select(state, appSessionId)?.[outputId],
  createSelectByAppSessionCellId: (appSessionCellId: AppSessionCellId) =>
    createShallowResultSelectorV2(
      selectOutputsWithResolvedContent,
      (state: RootState, appSessionId: AppSessionId) =>
        root.selectAppSessionCellIdToOutputIds(state, appSessionId)[
          appSessionCellId
        ],
      (entities, outputIds) =>
        outputIds
          ?.map((id) => entities?.[id])
          .filter(notEmpty)
          .sort((a, b) => a.order - b.order) ?? stableEmptyArray<OutputId>(),
    ),
};

const selectScopeItemsWithHidden = (
  state: RootState,
  appSessionId: AppSessionId,
) => root.safeSelect(state, appSessionId)?.scopeItems;
const selectScopeItems = createSelectorV2(
  selectScopeItemsWithHidden,
  (entities) =>
    entities == null ? null : omitBy(entities, (value) => value?.isHidden),
);

const scopeItems = {
  select: selectScopeItems,
  selectWithHidden: selectScopeItemsWithHidden,
  selectArray: createSelectorV2(selectScopeItems, (entities) =>
    entities != null
      ? typedObjectValues(entities).sort((a, b) => asciiCompare(a.name, b.name))
      : null,
  ),
  selectByName: (state: RootState, appSessionId: AppSessionId, name: string) =>
    selectScopeItemsWithHidden(state, appSessionId)?.[name],
};

export const appSessionEventStateSelectors = {
  ...root,
  appSession,
  appSessionCells,
  outputsWithContent,
  outputsWithResolvedContent,
  scopeItems,
};
